import { Link } from 'react-router-dom'
import { Calendar, Users, ArrowRight } from 'lucide-react'
import type { HostedEventSummary } from '@fellowship/shared'
import { EventBadge } from '../ui/EventBadge'
import { getEventType } from '../ui/eventTypes'
import { formatDate, GRADIENT } from './format'

export function HostedEventRow({ summary }: { summary: HostedEventSummary }) {
  const t = getEventType(summary.eventType)
  const isDraft = summary.status === 'draft'
  const hasUpcoming = summary.nextOccurrenceId !== null

  return (
    <article
      style={{
        display: 'flex',
        gap: 14,
        alignItems: 'flex-start',
        padding: '16px',
        background: 'var(--surface)',
        border: '1px solid var(--line-soft)',
        borderRadius: 16,
        borderLeft: `3px solid ${t.color}`,
      }}
    >
      <div style={{ flexShrink: 0, marginTop: 2 }}>
        <EventBadge type={summary.eventType} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <Link to={`/events/${summary.id}`} style={{ textDecoration: 'none' }}>
          <div
            style={{
              fontSize: 16,
              fontWeight: 600,
              color: 'var(--ink)',
              lineHeight: 1.25,
              marginBottom: 6,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{summary.title}</span>
            {isDraft && (
              <span
                style={{
                  flexShrink: 0,
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: 0.4,
                  textTransform: 'uppercase',
                  color: '#92400e',
                  background: '#fef3c7',
                  border: '1px solid #fde68a',
                  borderRadius: 999,
                  padding: '2px 8px',
                }}
              >
                Draft
              </span>
            )}
          </div>
        </Link>
        <div style={{ display: 'flex', gap: 12, fontSize: 12, color: 'var(--ink-2)', flexWrap: 'wrap' }}>
          {hasUpcoming ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <Calendar size={11} /> {formatDate(summary.nextOccurrenceStartsAt!)}
            </span>
          ) : (
            <span style={{ color: 'var(--muted)' }}>No upcoming dates</span>
          )}
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <Users size={11} /> {summary.rsvpCount} going
          </span>
          {summary.pendingCount > 0 && (
            <span style={{ fontWeight: 600, color: '#d97706' }}>{summary.pendingCount} pending</span>
          )}
        </div>
      </div>
      {isDraft ? (
        <Link
          to={`/events/${summary.id}`}
          style={{ textDecoration: 'none', flexShrink: 0 }}
          aria-label={`Edit draft ${summary.title}`}
        >
          <span
            style={{
              height: 32,
              padding: '0 14px',
              borderRadius: 999,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              fontSize: 12,
              fontWeight: 600,
              color: '#92400e',
              border: '1px solid #fde68a',
              background: '#fffbeb',
            }}
          >
            Edit draft <ArrowRight size={11} />
          </span>
        </Link>
      ) : hasUpcoming ? (
        <Link
          to={`/events/${summary.id}/occurrences/${summary.nextOccurrenceId}/manage`}
          style={{ textDecoration: 'none', flexShrink: 0 }}
          aria-label={`Manage guests for ${summary.title}`}
        >
          <span
            style={{
              height: 32,
              padding: '0 14px',
              borderRadius: 999,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              fontSize: 12,
              fontWeight: 600,
              color: '#fff',
              background: GRADIENT,
              boxShadow: '0 3px 8px rgba(59,76,202,.25)',
            }}
          >
            Manage <ArrowRight size={11} />
          </span>
        </Link>
      ) : (
        <Link to={`/events/${summary.id}`} style={{ textDecoration: 'none', flexShrink: 0 }}>
          <span
            style={{
              height: 32,
              padding: '0 12px',
              borderRadius: 999,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              fontSize: 12,
              fontWeight: 500,
              color: 'var(--ink-2)',
              border: '1px solid var(--line-soft)',
              background: 'transparent',
            }}
          >
            View <ArrowRight size={11} />
          </span>
        </Link>
      )}
    </article>
  )
}
